import dayjs from 'dayjs';
import _get from 'lodash/get';
import { v4 as uuidv4 } from 'uuid';
import ApiService from '@zola-helpers/client/dist/es/http/api';
import type {
  BusinessUnit,
  BusinessUnitComponent,
  UserContext,
} from '@zola-helpers/client/dist/es/@types';
import type { EventConfig } from '@zola/tracking-contracts/src/configs';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';
import { getCookie } from '@zola-helpers/client/dist/es/util/storage';
import { getDateUtc } from '@zola-helpers/client/dist/es/util/dateUtils';
import {
  fetchUserContext,
  identifyUserSailthru,
} from '@zola-helpers/client/dist/es/redux/user/userActions';
import {
  trackOnboardingCompleted,
  trackOnboardingStarted,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';
import { setGTMLoggedIn } from '@zola-helpers/client/dist/es/tracking/googleTagManager/googleTagManager';
import { trackUserCreated } from '@zola-helpers/client/dist/es/tracking/trackingHelper';
import { trackUserCreated as trackUserCreatedTrackingContracts } from '@zola/tracking-contracts/src/tracking';

type WeddingDetails = {
  date?: string | null;
  fullName?: string | null;
  hasNoPartner?: boolean;
  partnerFullName?: string | null;
  stillDeciding?: boolean;
};

type AccountDetails = {
  email: string | null;
  password?: string | null;
};

type AccountErrorMessages = {
  emptyPartnerWeddingDetails: string;
  emptyWeddingDetails: string;
  invalidEmail: string;
  invalidPassword: string;
  invalidWeddingDate: string;
  invalidWeddingDetails: string;
  inUseEmail: string;
};

type CreateAccountOptions = {
  accountDetails?: AccountDetails;
  businessCategory?: EventConfig['business_category'];
  businessUnitComponent?: string;
  businessUnitSource?: string;
  captchaToken?: string | null;
  currentErrors?: Record<string, unknown>;
  existingAccount?: Record<string, unknown>;
  isGuest?: boolean;
  shouldTrackOnboarding?: boolean;
  weddingDetails?: WeddingDetails;
};

type AccountData = {
  answers: unknown[];
  business_unit: string;
  captchaToken?: string | null;
  existing_account?: Record<string, unknown>;
  questionnaire_id: null;
  started_at: string;
  wedding_details: Record<string, unknown>;
  user?: {
    business_unit_source: string;
    email: string;
    password: string;
    timezone: string;
  };
};

const ACCOUNT_ERROR_MESSAGES: AccountErrorMessages = {
  invalidEmail: 'Please enter a correct email address',
  inUseEmail: 'The provided email is in use.',
  invalidPassword: 'Your password must be at least 8 characters long',
  emptyWeddingDetails: 'Please enter your first and last name',
  emptyPartnerWeddingDetails: "Please enter your partner's first and last name",
  invalidWeddingDate: 'Please make sure your wedding date is valid',
  invalidWeddingDetails: 'Please make sure your wedding details are valid.',
};

const DEFAULT_PARTNER = {
  firstName: 'Someone',
  lastName: 'Special',
};

const extractWeddingNames = (weddingDetails: WeddingDetails) => {
  // Validation
  let names = { errors: [] as string[] };

  if (!(weddingDetails.fullName && weddingDetails.fullName.trim().split(' ').length > 1)) {
    names = Object.assign(names, { errors: [...names.errors, 'userDetails'] });
  }

  if (
    !weddingDetails.hasNoPartner &&
    !(weddingDetails.partnerFullName && weddingDetails.partnerFullName.trim().split(' ').length > 1)
  ) {
    names = Object.assign(names, { errors: [...names.errors, 'partnerDetails'] });
  }

  if (names.errors.length > 0) {
    return names;
  }

  // Split user and partner values
  const [ownerUserFirstName, ...ownerUserLastNames] = (weddingDetails.fullName || '').split(' ');
  const [partnerUserFirstName, ...partnerUserLastNames] = weddingDetails.partnerFullName
    ? weddingDetails.partnerFullName.split(' ')
    : [DEFAULT_PARTNER.firstName, DEFAULT_PARTNER.lastName];

  return {
    owner_first_name: ownerUserFirstName,
    owner_last_name: ownerUserLastNames.join(' '),
    partner_first_name: partnerUserFirstName,
    partner_last_name: partnerUserLastNames.join(' '),
  };
};

const parseWeddingDate = (date: string) => {
  if (!date || date.length !== 10) {
    return null;
  }

  const parsedDate = dayjs(
    `${date.substring(0, 4)}-${date.substring(5, 7)}-${date.substring(8, 10)}`
  );

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate;
};

export const buildAccountDetails = (userContext: UserContext) => {
  if (!userContext) return {};
  // Some users can be registered without going through the onboarding flow
  // e.g. direct registry buying - creating account from checkout
  const { partner_first_name: partnerFirstName, partner_last_name: partnerLastName } =
    userContext.user_role_account_weddings?.[0] || {};
  const existingAccount = {
    account_id: userContext.user_role_account_weddings?.[0].account_id,
    user_id: userContext.object_id,
  };
  const weddingDetails = {
    fullName: `${userContext.first_name} ${userContext.last_name}`,
    partnerFullName: `${partnerFirstName} ${partnerLastName}`,
    hasNoPartner: !partnerFirstName,
  };
  const accountDetails = {
    email: userContext.email || '',
  };

  return {
    existingAccount,
    weddingDetails,
    accountDetails,
  };
};

const validateAccountFields = ({
  accountDetails,
  existingAccount,
  weddingNames,
  date,
  currentErrors = {},
}: {
  accountDetails: AccountDetails;
  existingAccount: Record<string, unknown>;
  weddingNames: Record<string, unknown>;
  date: string;
  currentErrors?: Record<string, unknown>;
}) => {
  const parsedDate = parseWeddingDate(date);
  let result = { errors: { ...currentErrors } };

  // Validate account (email + password)
  if (!existingAccount.account_id) {
    const isEmailInvalid =
      !accountDetails.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(accountDetails.email);
    const isPasswordInvalid = !accountDetails.password || accountDetails.password.length < 8;

    // Validate wedding details (user and partner infos + date)
    const isUserInvalid =
      weddingNames.errors && (weddingNames.errors as string[]).includes('userDetails');
    const isPartnerInvalid =
      weddingNames.errors && (weddingNames.errors as string[]).includes('partnerDetails');
    const isDateInvalid = (date && date.length > 0 && !parsedDate) || false;

    result = {
      errors: {
        ...result.errors,
        ...{
          account: {
            ...(result.errors.account || {}),
            email: {
              isInvalid: isEmailInvalid,
              errorMessage: ACCOUNT_ERROR_MESSAGES.invalidEmail,
            },
            password: {
              isInvalid: isPasswordInvalid,
              errorMessage: ACCOUNT_ERROR_MESSAGES.invalidPassword,
            },
          },
          weddingDetails: {
            user: {
              isInvalid: isUserInvalid,
              errorMessage: ACCOUNT_ERROR_MESSAGES.emptyWeddingDetails,
            },
            partner: {
              isInvalid: isPartnerInvalid,
              errorMessage: ACCOUNT_ERROR_MESSAGES.emptyPartnerWeddingDetails,
            },
            date: {
              isInvalid: isDateInvalid,
              errorMessage: ACCOUNT_ERROR_MESSAGES.invalidWeddingDate,
            },
          },
        },
      },
    };
  }

  return result;
};

/**
 * Ported from web-wedding to allow account creation with "null" names. An example use case is a
 * basic non-wedding paper user signing up without partner details (e.g. holiday and shower cards)
 */
export const createAccount = ({
  existingAccount = {},
  weddingDetails = {},
  accountDetails = {
    email: '',
  },
  businessCategory,
  businessUnitComponent = BUSINESS_UNIT.INVITATIONS,
  businessUnitSource = BUSINESS_UNIT.INVITATIONS,
  shouldTrackOnboarding,
  isGuest,
  currentErrors,
  captchaToken,
}: CreateAccountOptions) => {
  const weddingNames = extractWeddingNames(weddingDetails);
  const { date } = weddingDetails;
  const weddingDate = !weddingDetails.stillDeciding ? date : null;

  // Validation step
  const fieldsValidation = validateAccountFields({
    accountDetails,
    existingAccount,
    weddingNames,
    date: date || '',
    currentErrors,
  });

  const hasError = Object.keys(fieldsValidation.errors).find((entry) =>
    Object.keys(fieldsValidation.errors[entry] as Record<string, { isInvalid: boolean }>).some(
      (field) =>
        field !== 'emailInUse' &&
        (fieldsValidation.errors[entry] as Record<string, { isInvalid: boolean }>)[field].isInvalid
    )
  );

  if (hasError) {
    return Promise.resolve(fieldsValidation);
  }

  // Build data
  let accountData: AccountData = {
    business_unit: businessUnitComponent,
    questionnaire_id: null,
    started_at: getDateUtc().toISOString(),
    wedding_details: {
      ...weddingNames,
      event_date: weddingDate,
    },
    answers: [],
  };

  if (!existingAccount.account_id) {
    accountData = {
      ...accountData,
      captchaToken: captchaToken || null,
      user: {
        business_unit_source: businessUnitSource,
        email: accountDetails.email || '',
        password: accountDetails.password || '',
        timezone: dayjs?.tz?.guess() || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone,
      },
    };
  } else {
    accountData = {
      ...accountData,
      existing_account: existingAccount,
    };
  }

  // Send onboarding started legacy tracking event
  if (shouldTrackOnboarding) {
    trackOnboardingStarted(BUSINESS_UNIT.INVITATIONS, businessUnitComponent, !isGuest);
  }

  const onboardEndpoint = isGuest
    ? '/web-api/v1/onboard/submit'
    : '/web-api/v1/onboard/submit/existing';

  // Account creation request
  return ApiService.post(onboardEndpoint, accountData)
    .catch(() => {
      return {
        errors: {
          account: {
            emailInUse: {
              isInvalid: true,
              errorMessage: ACCOUNT_ERROR_MESSAGES.invalidWeddingDetails,
            },
          },
        },
      };
    })
    .then((result: { status?: string; error?: unknown } | unknown) => {
      if (
        (result as { status?: string; error?: unknown }).status === 'error' ||
        (result as { status?: string; error?: unknown }).error
      ) {
        const errorMessage = _get(result, ['error', 'message']);
        return {
          errors: {
            account: {
              emailInUse: {
                isInvalid: true,
                errorMessage: errorMessage || ACCOUNT_ERROR_MESSAGES.invalidWeddingDetails,
              },
            },
          },
        };
      }

      if (!existingAccount.account_id) {
        // Send onboarding created legacy tracking event
        if (shouldTrackOnboarding) {
          trackOnboardingCompleted(BUSINESS_UNIT.INVITATIONS, businessUnitComponent, {
            isExistingUser: false,
            weddingDate,
          });
        }

        // Analytics Logged in event
        setGTMLoggedIn();

        if (isGuest) {
          // Track user created
          let cookie: string | null = getCookie('pkey');
          if (cookie === '') {
            cookie = null;
          }
          trackUserCreated(accountDetails.email || '', cookie);

          // Tracking contracts events
          if (businessCategory) {
            const normalizedPkey = cookie || undefined;
            trackUserCreatedTrackingContracts({
              business_unit: 'PAPER',
              business_category: businessCategory,
              flow_run_id: uuidv4(),
              promotion_key: normalizedPkey,
            });
          }
        }

        // Subscription to Marketing Email Campaign Provider (Sailthru)
        // isGuest is determining if previously a guest or brand new user
        identifyUserSailthru(
          accountDetails.email || '',
          isGuest
            ? {
                business_unit_feature: businessUnitComponent as BusinessUnitComponent,
                business_unit_source: businessUnitSource as BusinessUnit,
              }
            : undefined
        ).catch(() => null);
      }

      return {};
    });
};

/**
 * Creates invitations account for existing user; to be used post-auth only
 */
export const createInvitationsAccount = (opts: Partial<CreateAccountOptions> = {}) => {
  return ApiService.get('/website-nav/web-api/v1/user/get').then((value: unknown) => {
    const userContext = value as UserContext;
    const { businessCategory, businessUnitComponent, businessUnitSource, shouldTrackOnboarding } =
      opts;

    const details = buildAccountDetails(userContext);

    return createAccount({
      isGuest: userContext.is_guest,
      existingAccount: details.existingAccount,
      weddingDetails: details.weddingDetails,
      accountDetails: details.accountDetails,
      businessCategory,
      businessUnitComponent,
      businessUnitSource,
      shouldTrackOnboarding,
    }).then((result) => {
      // Check if `errors` exist and are not empty
      if (result.errors && Object.keys(result.errors).length > 0) {
        return { errors: result.errors };
      }
      return {};
    });
  });
};

/**
 * Transfers drafts created with guest account to existing account.
 * Creates invitation account if not already created prior to transfering drafts.
 */
export const transferDraftsToExistingAccount = async (guestUserObjectId: string): Promise<void> => {
  const userContext = await fetchUserContext();
  const hasInvitationAccount = userContext.has_invitation_account;

  if (!hasInvitationAccount) {
    await createInvitationsAccount();
  }

  return ApiService.post('/web-api/v4/card-project/transfer-drafts', {
    guestUserObjectId,
  });
};
